import { Table, type TableColumnType, Tag, Typography } from 'antd';
import { FC } from 'react';
import { Link } from 'react-router-dom';

import { Schemas } from '@api-client/generated/types';
import { formatNumber } from '@constants';
import { formatReferenceDay } from '@utils';

import DifferenceTag from './DifferenceTag';
import { transactionsLink } from './transactionsLink';

const { Text } = Typography;

type IntervalWalkProps = {
  intervals: Schemas.ReconciliationInterval[];
  opening: Schemas.ReconciliationOpening;
  currencyCode: string;
  /** Both only so each line can open the transactions it was computed from. */
  companyId: string;
  accountId: string;
};

/**
 * The interval-by-interval working behind a row's verdict.
 *
 * Shown rather than summarised because the diagnosis is only as trustworthy as the arithmetic under
 * it, and because the pattern is what an accountant actually acts on: `+50.00` immediately followed
 * by `−50.00` is one transaction dated a day off, while a single figure that never comes back is a
 * transaction to go and find. Both are invisible in a seven-day total, which nets the first to zero.
 *
 * The first row is not an interval. It is what the walk started from — the opening balance against
 * every transaction we hold before it — and it leads because the intervals below only mean anything
 * relative to it: a walk that adds up perfectly from a figure that was already out by a thousand euros
 * has not shown that the account reconciles.
 */
const IntervalWalk: FC<IntervalWalkProps> = ({
  intervals,
  opening,
  currencyCode,
  companyId,
  accountId,
}) => {
  const money = (amount: number) =>
    formatNumber(amount, { style: 'currency', currency: currencyCode });

  type Row = {
    key: string;
    label: string;
    balance: number;
    transactions: number;
    difference: number;
    /** The transactions this line's figures were computed from. */
    href: string;
    matched?: boolean;
    isOpening?: boolean;
  };

  const rows: Row[] = [
    {
      key: 'opening',
      label: `up to ${formatReferenceDay(opening.upTo)}`,
      balance: opening.balance,
      transactions: opening.transactionsTotal,
      difference: opening.difference,
      // No lower bound, because neither has the figure: it is every transaction we hold up to that
      // day, however far back that reaches.
      href: transactionsLink({ companyId, accountId, to: opening.upTo }),
      isOpening: true,
    },
    ...intervals.map((interval) => ({
      key: `${interval.window.from}_${interval.window.to}`,
      label: `${formatReferenceDay(interval.window.from)} → ${formatReferenceDay(interval.window.to)}`,
      balance: interval.balanceDelta,
      transactions: interval.transactionsTotal,
      difference: interval.difference,
      href: transactionsLink({
        companyId,
        accountId,
        from: interval.window.from,
        to: interval.window.to,
      }),
      matched: interval.matched,
    })),
  ];

  const columns: TableColumnType<Row>[] = [
    {
      key: 'label',
      title: 'Interval',
      // Every line links to the transactions it was computed from — this is where the narrow question
      // belongs, beside the narrow figures. The row above links to the window as a whole.
      render: (_, row) =>
        row.isOpening ? (
          <Text type="secondary">
            Before the window — <Link to={row.href}>{row.label}</Link>
          </Text>
        ) : (
          <Link to={row.href}>{row.label}</Link>
        ),
    },
    {
      key: 'balance',
      title: 'Balance moved',
      align: 'right',
      // The opening row holds a *level*, not a movement — the balance the walk started from — so it
      // says which it is. Under a header reading "Balance moved", a bare €1,500.00 there claims the
      // balance moved by the whole of itself.
      render: (_, row) =>
        row.isOpening ? (
          <Text type="secondary">balance {money(row.balance)}</Text>
        ) : (
          money(row.balance)
        ),
    },
    {
      key: 'transactions',
      title: 'Transactions',
      align: 'right',
      // Everything we hold up to that day, not the movements of one interval — the same qualification
      // for the same reason.
      render: (_, row) =>
        row.isOpening ? (
          <Text type="secondary">all of them: {money(row.transactions)}</Text>
        ) : (
          money(row.transactions)
        ),
    },
    {
      key: 'difference',
      title: 'Unexplained',
      align: 'right',
      // `matched` rather than `difference === 0`: the verdict was decided on internal units before
      // the figure beside it was divided back out.
      //
      // The opening row is never given a verdict. Its difference is an account we were connected to
      // partway through its life as much as it is a discrepancy, and marking it red would put a fault
      // on every row on the page.
      render: (_, row) => {
        if (row.isOpening) {
          return row.difference === 0 ? (
            <Text type="secondary">nothing outstanding</Text>
          ) : (
            <Tag>{money(row.difference)}</Tag>
          );
        }

        return row.matched ? (
          <Tag color="green">adds up</Tag>
        ) : (
          <DifferenceTag
            currencyCode={currencyCode}
            difference={row.difference}
          />
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={rows}
      pagination={false}
      rowKey={(row) => row.key}
      size="small"
    />
  );
};

export default IntervalWalk;
